import { Injectable, OnDestroy } from '@angular/core';
import { FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { FormsBase } from 'src/app/common-components/forms-base/forms-base';
import { containsCharactersValidator } from 'src/app/common-components/validators/contain-characters.validator';
import { getPrivileges } from '../../role-details/state/role-detail.selectors';
import { UserDetailFormValue } from '../models/user-detail-form';
import { UserDetailSaveModel } from '../models/user-detail-save.model';
import { UserDetailView } from '../models/user-detail-view.model';

@Injectable()
export class UserDetailFormService extends FormsBase implements OnDestroy {
   private destroyed$ = new Subject<void>();

   constructor(fb: UntypedFormBuilder, private store: Store, private actions: Actions) {
      super();

      this.form = fb.group({
         account: fb.control(null),
         emailAddress: fb.control(null, [
            Validators.required,
            Validators.pattern(/^\s*([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\s*$/i),
         ]),
         extension: fb.control(null),
         isActive: fb.control(null),
         isAdmin: fb.control(null),
         isSystemUser: fb.control(null),
         name: fb.control(null, [Validators.required, Validators.maxLength(255), containsCharactersValidator()]),
         roles: fb.control(null),
         username: fb.control(null, [Validators.maxLength(255), containsCharactersValidator()]),
         userReports: fb.control(null),
         userPrivileges: fb.group({}),
         userRecordingExtensions: fb.control(null),
      });
      const userPrivilegesArray = this.form.get('userPrivileges') as FormGroup;
      this.store.select(getPrivileges).subscribe((privileges) => {
         if (privileges) {
            privileges.forEach((privilege) => {
               userPrivilegesArray.addControl(
                  privilege.privilegeId?.toString() ?? '',
                  fb.group({
                     userPrivilegeId: fb.control(null),
                     privilegeId: fb.control(privilege.privilegeId),
                     isAdd: fb.control(null),
                     isDelete: fb.control(null),
                     isEdit: fb.control(null),
                     isEnabled: fb.control(null),
                     isView: fb.control(null),
                  })
               );
            });
         }
      });
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   updateUser(user: UserDetailView): void {
      if (!user) {
         return;
      }

      this.resetUserPrivilegesFormGroup();

      const formValue: Partial<UserDetailFormValue> = {
         account: user.account,
         emailAddress: user.emailAddress,
         extension: user.extension,
         isActive: user.isActive,
         isAdmin: user.isAdmin,
         isSystemUser: user.isSystemUser,
         roles: user.rolesConnection?.items.map((userRole) => {
            return {
               name: userRole.role.name,
               roleId: userRole.role.roleId,
               userId: user.userId,
               userRoleId: userRole.userRoleId,
            };
         }),
         userReports: user.userReports,
         userPrivileges: user.privileges,
         userRecordingExtensions: user.userRecordingExtensions,
         name: user.name,
         username: user.username,
      };

      this.form.patchValue(formValue);
      this.markAsUntouchedAndPristine();
   }

   getValue(userId: number | null): UserDetailSaveModel {
      const value = this.form.value as UserDetailFormValue;

      return {
         accountId: value.account ? value.account.accountId : null,
         emailAddress: value.emailAddress ?? null,
         extensionId: value.extension?.extensionId ?? null,
         isActive: value.isActive,
         isPasswordMigrated: true,
         isAdmin: value.isAdmin,
         isSystemUser: value.isSystemUser,
         name: value.name,
         roles: value.roles,
         userId,
         username: value.username,
         userReports: this.form.get('userReports')?.value,
         userPrivileges: Object.values(value.userPrivileges).map((userPrivilege) => {
            return {
               userPrivilegeId: userPrivilege.userPrivilegeId ?? 0,
               accountId: userPrivilege.accountId,
               isAdd: userPrivilege.isAdd,
               isDelete: userPrivilege.isDelete,
               isEdit: userPrivilege.isEdit,
               isEnabled: userPrivilege.isEnabled,
               isView: userPrivilege.isView,
               privilegeId: userPrivilege.privilegeId,
               userId: userId,
            };
         }),
         userCallExtensionRecordingPrivileges: value.userRecordingExtensions?.extensionRecordingPermissions?.map(
            (recordingPrivilege) => {
               return {
                  extensionId: recordingPrivilege.extension.extensionId,
                  isDeleted: recordingPrivilege.isDeleted,
                  isInclude: recordingPrivilege.isInclude,
                  userCallRecordingExtensionId: recordingPrivilege.id ?? 0,
                  userId: userId ?? 0,
               };
            }
         ),
         userCallExtensionGroupRecordingPrivileges:
            value.userRecordingExtensions?.extensionGroupRecordingPermissions?.map((recordingPrivilege) => {
               return {
                  extensionGroupId: recordingPrivilege.extensionGroup.extensionGroupId,
                  isDeleted: recordingPrivilege.isDeleted,
                  isInclude: recordingPrivilege.isInclude,
                  userCallRecordingExtensionGroupId: recordingPrivilege.id ?? 0,
                  userId: userId ?? 0,
               };
            }),
         hasAllExtensionRecordings: value.userRecordingExtensions?.allExtensionsIsSelected ?? false,
      };
   }

   resetUserPrivilegesFormGroup(): void {
      const userPrivileges = this.form.get('userPrivileges') as FormGroup;
      Object.keys(userPrivileges.controls).forEach((control) => {
         const userPrivilege = userPrivileges.get(control) as FormGroup;
         userPrivilege.patchValue({
            userPrivilegeId: null,
            isAdd: null,
            isDelete: null,
            isEdit: null,
            isEnabled: null,
            isView: null,
         });
      });
   }
}
